import { Routes } from '@angular/router';
import { LoginComponent } from './core/auth/login.component';
import { authGuard, permissionGuard, mustChangePasswordGuard } from './core/auth/auth.guard';
// Customer Support Center (CSC) — config objects for the shared master/report screens
// (type-only component imports inside are erased, so the components stay lazy-loaded).
import { CSC_CATEGORIES, CSC_PRIORITIES, CSC_STATUSES, CSC_AGENTS, CSC_SLA, CSC_CANNED, CSC_KB, CSC_REP_TICKETS, CSC_REP_AGENT, CSC_REP_SLA } from './modules/csc/_shared/csc-configs';

// ============================================================================
// Route table.
//
// PHASE 1 — Transaction Purchase Sales Stock > Core (7 modules whose
// Controller/Entity are already compiled into the API and whose DbSets
// already exist in AppDbContext.cs — see LogixAI.Api.csproj header).
//
// Components now live PHYSICALLY inside src/app/modules/transaction/<module>/
// (moved in per Sir Rashid's instruction, so @angular/core and node_modules
// resolve correctly — previously they were imported by relative path from
// outside the Frontend project root, which broke module resolution).
//
// PHASE 2 — Configuration, Finance, Finance Reports: added below as their
// frontend files are copied into src/app/modules/<section>/<module>/ and
// wired into the backend.
// ============================================================================
export const routes: Routes = [
  // Public login screen (renders full-screen; the shell is hidden until logged in).
  { path: 'login', component: LoginComponent, title: 'Sign in · Logix AI ERP' },

  // Everything else requires an authenticated session (Rec-0 Identity).
  {
    path: '',
    canActivateChild: [authGuard, permissionGuard, mustChangePasswordGuard],
    children: [
  { path: '', title: 'Dashboard · Logix AI ERP', loadComponent: () => import('./features/dashboard/dashboard.component').then(m => m.DashboardComponent) },

  // ---------------- Administration (User Setup, 2026-08-07) — admin-gated console ----------------
  { path: 'administration/users', title: 'Users · Administration', loadComponent: () => import('./modules/administration/users/users.component').then(m => m.AdminUsersComponent) },
  { path: 'administration/roles', title: 'Roles · Administration', loadComponent: () => import('./modules/administration/roles/roles.component').then(m => m.AdminRolesComponent) },
  { path: 'administration/departments', title: 'Departments · Administration', loadComponent: () => import('./modules/administration/departments/departments.component').then(m => m.AdminDepartmentsComponent) },
  { path: 'administration/reports/user-access', title: 'User Access Report · Administration', loadComponent: () => import('./modules/administration/reports/user-access-report.component').then(m => m.UserAccessReportComponent) },

  // ---------------- Sales Targets & Team Performance (2026-08-07) ----------------
  { path: 'sales-performance/dashboard', title: 'Sales Performance', loadComponent: () => import('./modules/sales-performance/dashboard/dashboard.component').then(m => m.SalesPerformanceDashboardComponent) },
  { path: 'sales-performance/teams', title: 'Sales Teams', loadComponent: () => import('./modules/sales-performance/teams/teams.component').then(m => m.SalesTeamsComponent) },
  { path: 'sales-performance/quotas', title: 'Quota Allocation', loadComponent: () => import('./modules/sales-performance/quotas/quotas.component').then(m => m.SalesQuotasComponent) },
  // Available to every signed-in user (personal).
  { path: 'account/change-password', title: 'Change Password', loadComponent: () => import('./modules/account/change-password/change-password.component').then(m => m.ChangePasswordComponent) },

  // ---------------- Transaction Purchase Sales Stock > Core ----------------
  {
    path: 'pss/core/uom',
    title: 'Units of Measurement',
    loadComponent: () =>
      import('./modules/transaction/uom/UI_Component').then(m => m.UOMComponent)
  },
  {
    path: 'pss/core/uom-group',
    title: 'UOM Group',
    loadComponent: () =>
      import('./modules/transaction/uom-group/UI_Component').then(m => m.UOMGroupComponent)
  },
  {
    path: 'pss/core/customer-group',
    title: 'Customer Group',
    loadComponent: () =>
      import('./modules/transaction/customer-group/UI_Component').then(m => m.CustomerGroupComponent)
  },
  {
    path: 'pss/core/payment-term',
    title: 'Payment Term',
    loadComponent: () =>
      import('./modules/transaction/payment-term/UI_Component').then(m => m.PaymentTermComponent)
  },
  {
    path: 'pss/core/price-list',
    title: 'Price List',
    loadComponent: () =>
      import('./modules/transaction/price-list/UI_Component').then(m => m.PriceListComponent)
  },
  {
    path: 'pss/core/product-attributes',
    title: 'Product Attributes',
    loadComponent: () =>
      import('./modules/transaction/product-attributes/UI_Component').then(m => m.ProductAttributesComponent)
  },
  {
    path: 'pss/core/product-company',
    title: 'Product Company',
    loadComponent: () =>
      import('./modules/transaction/product-company/UI_Component').then(m => m.ProductCompanyComponent)
  },
  {
    path: 'pss/product-management/products',
    title: 'Products',
    loadComponent: () =>
      import('./modules/transaction/products/UI_Component').then(m => m.ProductsComponent)
  },
  {
    path: 'pss/product-management/product-relocation',
    title: 'Product Relocation',
    loadComponent: () =>
      import('./modules/transaction/product-relocation/UI_Component').then(m => m.ProductRelocationComponent)
  },
  {
    path: 'pss/product-management/product-in-house-use',
    title: 'Product In-house Use',
    loadComponent: () =>
      import('./modules/transaction/product-in-house-use/UI_Component').then(m => m.ProductInHouseUseComponent)
  },
  {
    path: 'pss/product-management/product-reconciliation',
    title: 'Product Reconciliation',
    loadComponent: () =>
      import('./modules/transaction/product-reconciliation/UI_Component').then(m => m.ProductReconciliationComponent)
  },
  {
    path: 'pss/product-management/product-damage',
    title: 'Product Damage',
    loadComponent: () =>
      import('./modules/transaction/product-damage/UI_Component').then(m => m.ProductDamageComponent)
  },
  {
    path: 'pss/product-management/product-opening-stock',
    title: 'Product Opening Stock',
    loadComponent: () =>
      import('./modules/transaction/product-opening-stock/UI_Component').then(m => m.ProductOpeningStockComponent)
  },
  {
    path: 'pss/product-management/product-categories',
    title: 'Product Categories',
    loadComponent: () =>
      import('./modules/transaction/product-categories/UI_Component').then(m => m.ProductCategoriesComponent)
  },
  {
    path: 'pss/product-management/add-on-group',
    title: 'Add On Group',
    loadComponent: () =>
      import('./modules/transaction/product-add-on-group/UI_Component').then(m => m.ProductAddOnGroupComponent)
  },
  {
    path: 'pss/product-management/category-ingredient',
    title: 'Category Ingredient',
    loadComponent: () =>
      import('./modules/transaction/product-category-ingredient/UI_Component').then(m => m.ProductCategoryIngredientComponent)
  },
  {
    path: 'pss/product-management/product-taxation',
    title: 'Product Taxation',
    loadComponent: () =>
      import('./modules/transaction/product-taxation/UI_Component').then(m => m.ProductTaxationComponent)
  },
  {
    path: 'pss/product-management/costing-estimation-recipe',
    title: 'Costing & Estimation | Recipe',
    loadComponent: () =>
      import('./modules/transaction/product-costing-recipe/UI_Component').then(m => m.ProductCostingRecipeComponent)
  },
  {
    path: 'pss/product-management/product-updation',
    title: 'Product Updation',
    loadComponent: () =>
      import('./modules/transaction/product-updation/UI_Component').then(m => m.ProductUpdationComponent)
  },

  // ===================== Transaction PSS > Setting (Phase 8) =====================
  {
    path: 'pss/setting/category-step-code',
    title: 'Category / Product Step Code',
    loadComponent: () =>
      import('./modules/transaction/setting-category-step-code/UI_Component').then(m => m.SettingCategoryStepCodeComponent)
  },
  {
    path: 'pss/setting/warehouse-step-code',
    title: 'Warehouse Step Code',
    loadComponent: () =>
      import('./modules/transaction/setting-warehouse-step-code/UI_Component').then(m => m.SettingWarehouseStepCodeComponent)
  },
  {
    path: 'pss/setting/channel-prices',
    title: 'Channel Prices',
    loadComponent: () =>
      import('./modules/transaction/setting-channel-prices/UI_Component').then(m => m.SettingChannelPricesComponent)
  },
  {
    path: 'pss/setting/payment-method',
    title: 'Payment Method',
    loadComponent: () =>
      import('./modules/transaction/setting-payment-method/UI_Component').then(m => m.SettingPaymentMethodComponent)
  },
  {
    path: 'pss/setting/code-formats',
    title: 'Code Formats',
    loadComponent: () =>
      import('./modules/transaction/setting-code-formats/UI_Component').then(m => m.SettingCodeFormatsComponent)
  },
  {
    path: 'pss/setting/procurement-settings',
    title: 'Procurement Settings',
    loadComponent: () =>
      import('./modules/transaction/setting-procurement-settings/UI_Component').then(m => m.SettingProcurementSettingsComponent)
  },
  {
    path: 'pss/setting/procurement-posting',
    title: 'Procurement Posting',
    loadComponent: () =>
      import('./modules/transaction/setting-procurement-posting/UI_Component').then(m => m.SettingProcurementPostingComponent)
  },
  {
    path: 'pss/setting/procurement-report-setup',
    title: 'Procurement Report Setup',
    loadComponent: () =>
      import('./modules/transaction/setting-procurement-report-setup/UI_Component').then(m => m.SettingProcurementReportSetupComponent)
  },
  {
    path: 'pss/setting/purchase-account-mapping',
    title: 'Purchase Account Mapping',
    loadComponent: () =>
      import('./modules/transaction/setting-purchase-account-mapping/UI_Component').then(m => m.SettingPurchaseAccountMappingComponent)
  },
  {
    path: 'pss/setting/sales-account-mapping',
    title: 'Sales Account Mapping',
    loadComponent: () =>
      import('./modules/transaction/setting-sales-account-mapping/UI_Component').then(m => m.SettingSalesAccountMappingComponent)
  },
  {
    path: 'pss/setting/customer-credit-recharge',
    title: 'Customer Credit Recharge',
    loadComponent: () =>
      import('./modules/transaction/setting-customer-credit-recharge/UI_Component').then(m => m.SettingCustomerCreditRechargeComponent)
  },

  // ===================== Transaction PSS > Labeling (Phase 9) =====================
  {
    path: 'pss/labeling/label-designing',
    title: 'Label Designing',
    loadComponent: () =>
      import('./modules/transaction/label-designing/UI_Component').then(m => m.LabelDesigningComponent)
  },
  {
    path: 'pss/labeling/label-printing',
    title: 'Label Printing',
    loadComponent: () =>
      import('./modules/transaction/label-printing/UI_Component').then(m => m.LabelPrintingComponent)
  },

  // ===================== Transaction PSS > Tracking (Phase 9) =====================
  {
    path: 'pss/tracking/orders-tracking',
    title: 'Orders Tracking',
    loadComponent: () =>
      import('./modules/transaction/tracking-orders/UI_Component').then(m => m.TrackingOrdersComponent)
  },
  {
    path: 'pss/tracking/vendor-directory',
    title: 'Vendor Directory',
    loadComponent: () =>
      import('./modules/transaction/tracking-vendor-directory/UI_Component').then(m => m.TrackingVendorDirectoryComponent)
  },
  {
    path: 'pss/tracking/customer-directory',
    title: 'Customer Directory',
    loadComponent: () =>
      import('./modules/transaction/tracking-customer-directory/UI_Component').then(m => m.TrackingCustomerDirectoryComponent)
  },
  {
    path: 'pss/tracking/good-delivery-note-directory',
    title: 'Good Delivery Note Directory',
    loadComponent: () =>
      import('./modules/transaction/tracking-gdn-directory/UI_Component').then(m => m.TrackingGdnDirectoryComponent)
  },
  {
    path: 'pss/tracking/goods-received-receipt-directory',
    title: 'Goods Received Receipt Directory',
    loadComponent: () =>
      import('./modules/transaction/tracking-grr-directory/UI_Component').then(m => m.TrackingGrrDirectoryComponent)
  },

  // ===================== Transaction PSS > Loyalty Management (Phase 10) =====================
  {
    path: 'pss/loyalty-management/loyalty',
    title: 'Loyalty',
    loadComponent: () =>
      import('./modules/transaction/loyalty/UI_Component').then(m => m.LoyaltyComponent)
  },
  {
    path: 'pss/loyalty-management/loyalty-card',
    title: 'Loyalty Card',
    loadComponent: () =>
      import('./modules/transaction/loyalty-card/UI_Component').then(m => m.LoyaltyCardComponent)
  },
  {
    path: 'pss/loyalty-management/loyalty-history',
    title: 'Loyalty History',
    loadComponent: () =>
      import('./modules/transaction/loyalty-history/UI_Component').then(m => m.LoyaltyHistoryComponent)
  },
  {
    path: 'pss/loyalty-management/history-report',
    title: 'Loyalty History Report',
    loadComponent: () =>
      import('./modules/transaction/loyalty-history-report/UI_Component').then(m => m.LoyaltyHistoryReportComponent)
  },

  // ===================== Transaction PSS > Import Export (Phase 10) =====================
  {
    path: 'pss/import-export/letter-of-credit',
    title: 'Letter of Credit',
    loadComponent: () =>
      import('./modules/transaction/letter-of-credit/UI_Component').then(m => m.LetterOfCreditComponent)
  },
  {
    path: 'pss/import-export/customs-trade-expenses',
    title: 'Customs & Trade Expenses',
    loadComponent: () =>
      import('./modules/transaction/customs-trade-expenses/UI_Component').then(m => m.CustomsTradeExpensesComponent)
  },
  {
    path: 'pss/import-export/performa-invoice',
    title: 'Performa Invoice',
    loadComponent: () =>
      import('./modules/transaction/performa-invoice/UI_Component').then(m => m.PerformaInvoiceComponent)
  },
  {
    path: 'pss/import-export/import-costing-valuation',
    title: 'Import Costing & Valuation',
    loadComponent: () =>
      import('./modules/transaction/import-costing-valuation/UI_Component').then(m => m.ImportCostingValuationComponent)
  },

  {
    path: 'pss/core/projects',
    title: 'Projects',
    loadComponent: () =>
      import('./modules/transaction/projects/UI_Component').then(m => m.ProjectsComponent)
  },
  {
    path: 'pss/core/warehouse',
    title: 'Warehouse',
    loadComponent: () =>
      import('./modules/transaction/warehouse/UI_Component').then(m => m.WarehouseComponent)
  },
  {
    path: 'pss/core/sales-region',
    title: 'Sales Region',
    loadComponent: () =>
      import('./modules/transaction/sales-region/UI_Component').then(m => m.SalesRegionComponent)
  },
  {
    path: 'pss/core/commission-setup',
    title: 'Commission Setup',
    loadComponent: () =>
      import('./modules/transaction/commission-setup/UI_Component').then(m => m.CommissionSetupComponent)
  },
  {
    path: 'pss/core/locations',
    title: 'Locations',
    loadComponent: () =>
      import('./modules/transaction/locations/UI_Component').then(m => m.LocationsComponent)
  },
  {
    path: 'pss/core/shipments',
    title: 'Shipments',
    loadComponent: () =>
      import('./modules/transaction/shipments/UI_Component').then(m => m.ShipmentsComponent)
  },

  // ---------------- Transaction Purchase Sales Stock > Business Partners ----------------
  {
    path: 'pss/business-partners/vendors',
    title: 'Vendors',
    loadComponent: () =>
      import('./modules/transaction/vendors/UI_Component').then(m => m.VendorsComponent)
  },
  {
    path: 'pss/business-partners/customers',
    title: 'Customers',
    loadComponent: () =>
      import('./modules/transaction/customers/UI_Component').then(m => m.CustomersComponent)
  },
  {
    path: 'pss/business-partners/sms-whatsapp',
    title: 'SMS / WhatsApp',
    loadComponent: () =>
      import('./modules/transaction/sms-whatsapp/UI_Component').then(m => m.SmsWhatsAppComponent)
  },
  {
    path: 'pss/business-partners/sales-agent',
    title: 'Sales Agent',
    loadComponent: () =>
      import('./modules/transaction/sales-agent/UI_Component').then(m => m.SalesAgentComponent)
  },
  {
    path: 'pss/business-partners/collection-agent',
    title: 'Collection Agent Policy at recovery %',
    loadComponent: () =>
      import('./modules/transaction/collection-agent/UI_Component').then(m => m.CollectionAgentComponent)
  },
  {
    path: 'pss/purchasing-ap/purchase-orders',
    title: 'Purchase Orders',
    loadComponent: () =>
      import('./modules/transaction/purchase-orders/UI_Component').then(m => m.PurchaseOrdersComponent)
  },
  {
    path: 'pss/purchasing-ap/goods-received-note',
    title: 'Goods Received Note',
    loadComponent: () =>
      import('./modules/transaction/goods-received-note/UI_Component').then(m => m.GoodsReceivedNoteComponent)
  },
  {
    path: 'pss/purchasing-ap/purchase-requisition',
    title: 'Purchase Requisition',
    loadComponent: () =>
      import('./modules/transaction/purchase-requisition/UI_Component').then(m => m.PurchaseRequisitionComponent)
  },
  {
    path: 'pss/purchasing-ap/purchase-bill',
    title: 'Purchase Bill',
    loadComponent: () =>
      import('./modules/transaction/purchase-bill/UI_Component').then(m => m.PurchaseBillComponent)
  },
  {
    path: 'pss/purchasing-ap/purchase-return',
    title: 'Purchase Return',
    loadComponent: () =>
      import('./modules/transaction/purchase-return/UI_Component').then(m => m.PurchaseReturnComponent)
  },
  {
    path: 'pss/purchasing-ap/auto-purchase-order',
    title: 'Auto Purchase Order',
    loadComponent: () =>
      import('./modules/transaction/auto-purchase-order/UI_Component').then(m => m.AutoPurchaseOrderComponent)
  },
  {
    path: 'pss/purchasing-ap/inward-gate-pass',
    title: 'Inward Gate Pass',
    loadComponent: () =>
      import('./modules/transaction/inward-gate-pass/UI_Component').then(m => m.InwardGatePassComponent)
  },
  {
    path: 'pss/purchasing-ap/outward-gate-pass',
    title: 'Outward Gate Pass',
    loadComponent: () =>
      import('./modules/transaction/outward-gate-pass/UI_Component').then(m => m.OutwardGatePassComponent)
  },
  {
    path: 'pss/purchasing-ap/payments-on-credit-bill',
    title: 'Payments on Credit Bill',
    loadComponent: () =>
      import('./modules/transaction/payments-on-credit-bill/UI_Component').then(m => m.PaymentsOnCreditBillComponent)
  },

  // ---------------- Transaction Purchase Sales Stock > Sales-AR ----------------
  // 7 line-item sale documents, all riding the shared SalesDocument engine
  // (thin controllers). Listed in AR-chain order:
  // Quotation -> Work Orders -> Good Delivery Note -> Invoice -> Refunds -> Gate Passes.
  {
    path: 'pss/sales-ar/sales-quotation',
    title: 'Sales Quotation',
    loadComponent: () =>
      import('./modules/transaction/sales-quotation/UI_Component').then(m => m.SalesQuotationComponent)
  },
  {
    path: 'pss/sales-ar/work-orders',
    title: 'Work Orders',
    loadComponent: () =>
      import('./modules/transaction/work-orders/UI_Component').then(m => m.WorkOrdersComponent)
  },
  {
    path: 'pss/sales-ar/good-delivery-note',
    title: 'Good Delivery Note',
    loadComponent: () =>
      import('./modules/transaction/good-delivery-note/UI_Component').then(m => m.GoodDeliveryNoteComponent)
  },
  {
    path: 'pss/sales-ar/sales-invoice',
    title: 'Sales Invoice',
    loadComponent: () =>
      import('./modules/transaction/sales-invoice/UI_Component').then(m => m.SalesInvoiceComponent)
  },
  {
    path: 'pss/sales-ar/sales-refunds',
    title: 'Sales Refunds',
    loadComponent: () =>
      import('./modules/transaction/sales-refunds/UI_Component').then(m => m.SalesRefundsComponent)
  },
  {
    path: 'pss/sales-ar/inward-gate-pass',
    title: 'Inward Gate Pass',
    loadComponent: () =>
      import('./modules/transaction/sale-inward-gate-pass/UI_Component').then(m => m.SaleInwardGatePassComponent)
  },
  {
    path: 'pss/sales-ar/outward-gate-pass',
    title: 'Outward Gate Pass',
    loadComponent: () =>
      import('./modules/transaction/sale-outward-gate-pass/UI_Component').then(m => m.SaleOutwardGatePassComponent)
  },
  {
    path: 'pss/sales-ar/collection-on-credit-bill',
    title: 'Collection on Credit Bill',
    loadComponent: () =>
      import('./modules/transaction/collection-on-credit-bill/UI_Component').then(m => m.CollectionOnCreditBillComponent)
  },
  {
    path: 'pss/sales-ar/sales-agent-dashboard',
    title: 'Sales Agent Dashboard',
    loadComponent: () =>
      import('./modules/transaction/sales-agent-dashboard/UI_Component').then(m => m.SalesAgentDashboardComponent)
  },

  // ---------------- Configuration > Company ----------------
  {
    path: 'configuration/company/email',
    title: 'Email Settings',
    loadComponent: () =>
      import('./modules/configuration/company/email/UI_Component').then(m => m.CompanyEmailComponent)
  },
  {
    path: 'configuration/company/fiscal-span',
    title: 'Fiscal Span',
    loadComponent: () =>
      import('./modules/configuration/company/fiscal-span/UI_Component').then(m => m.FiscalSpanComponent)
  },
  {
    path: 'configuration/company/profile',
    title: 'Company Profile',
    loadComponent: () =>
      import('./modules/configuration/company/profile/UI_Component').then(m => m.CompanyProfileComponent)
  },
  {
    path: 'configuration/company/settings',
    title: 'Company Settings',
    loadComponent: () =>
      import('./modules/configuration/company/settings/UI_Component').then(m => m.CompanySettingsComponent)
  },

  // ---------------- Configuration > Management ----------------
  {
    path: 'configuration/management/currency',
    title: 'Currency',
    loadComponent: () =>
      import('./modules/configuration/management/currency/UI_Component').then(m => m.CurrencyComponent)
  },
  {
    path: 'configuration/management/naming-conventions',
    title: 'Naming Conventions',
    loadComponent: () =>
      import('./modules/configuration/management/naming-conventions/UI_Component').then(m => m.NamingConventionsComponent)
  },
  {
    path: 'configuration/management/pending-approvals',
    title: 'Pending Approvals',
    loadComponent: () =>
      import('./modules/configuration/management/pending-approvals/UI_Component').then(m => m.PendingApprovalsComponent)
  },

  // ---------------- Configuration > Security ----------------
  {
    path: 'configuration/security/activity-tracing',
    title: 'Activity Tracing',
    loadComponent: () =>
      import('./modules/configuration/security/activity-tracing/UI_Component').then(m => m.ActivityTracingComponent)
  },
  {
    path: 'configuration/security/privileges',
    title: 'Role Permissions',
    loadComponent: () =>
      import('./modules/configuration/security/privileges/UI_Component').then(m => m.PrivilegesComponent)
  },
  // NOTE: the old Configuration > Security > Users screen is superseded by the
  // Administration > Users console (/administration/users), which adds role-level,
  // department scoping and the lifecycle status. Route removed to avoid two editors.

  // ---------------- Configuration > Tax Setup ----------------
  {
    path: 'configuration/tax-setup/tax-group',
    title: 'Tax Group',
    loadComponent: () =>
      import('./modules/configuration/tax-setup/tax-group/UI_Component').then(m => m.TaxGroupComponent)
  },
  {
    path: 'configuration/tax-setup/taxes',
    title: 'Taxes',
    loadComponent: () =>
      import('./modules/configuration/tax-setup/taxes/UI_Component').then(m => m.TaxesComponent)
  },
  {
    path: 'configuration/tax-setup/tax-authorities',
    title: 'Tax Authorities',
    loadComponent: () =>
      import('./modules/configuration/tax-setup/tax-authorities/UI_Component').then(m => m.TaxAuthoritiesComponent)
  },
  {
    path: 'configuration/tax-setup/tax-rate-history',
    title: 'Tax Rate History',
    loadComponent: () =>
      import('./modules/configuration/tax-setup/tax-rate-history/UI_Component').then(m => m.TaxRateHistoryComponent)
  },
  {
    path: 'configuration/tax-setup/withholding-tax',
    title: 'Withholding Tax',
    loadComponent: () =>
      import('./modules/configuration/tax-setup/withholding-tax/UI_Component').then(m => m.WithholdingTaxComponent)
  },
  {
    path: 'configuration/tax-setup/tax-register',
    title: 'Tax Register',
    loadComponent: () =>
      import('./modules/configuration/tax-setup/tax-register/UI_Component').then(m => m.TaxRegisterComponent)
  },
  {
    path: 'configuration/tax-setup/fbr-settings',
    title: 'FBR Digital Invoicing Settings',
    loadComponent: () =>
      import('./modules/configuration/tax-setup/fbr-settings/UI_Component').then(m => m.FbrSettingsComponent)
  },
  {
    path: 'configuration/tax-setup/tax-exemptions',
    title: 'Tax Exemptions & SRO Tariff',
    loadComponent: () =>
      import('./modules/configuration/tax-setup/tax-exemptions/UI_Component').then(m => m.TaxExemptionsComponent)
  },
  {
    path: 'configuration/tax-setup/fbr-returns',
    title: 'FBR Tax Returns & Statements',
    loadComponent: () =>
      import('./modules/configuration/tax-setup/fbr-returns/UI_Component').then(m => m.FbrReturnsComponent)
  },

  // ================= Finance Reports > Audit Reports =================
  {
    path: 'finance-reports/audit-reports/general-records',
    title: 'General Records',
    loadComponent: () =>
      import('./modules/finance-reports/audit-reports/general-records/UI_Component').then(m => m.AuditGeneralRecordsComponent)
  },
  {
    path: 'finance-reports/audit-reports/trial-balance',
    title: 'Trial Balance',
    loadComponent: () =>
      import('./modules/finance-reports/audit-reports/trial-balance/UI_Component').then(m => m.TrialBalanceComponent)
  },

  // ========== Finance Reports > Cost Centers Group Reports ==========
  {
    path: 'finance-reports/cost-centers-group-reports/budget-monthly-analysis',
    title: 'Budget Monthly Analysis',
    loadComponent: () =>
      import('./modules/finance-reports/cost-centers-group-reports/budget-monthly-analysis/UI_Component').then(m => m.BudgetMonthlyAnalysisComponent)
  },
  {
    path: 'finance-reports/cost-centers-group-reports/cost-center-account-summary',
    title: 'Cost Center Account Summary',
    loadComponent: () =>
      import('./modules/finance-reports/cost-centers-group-reports/cost-center-account-summary/UI_Component').then(m => m.CostCenterAccountSummaryComponent)
  },
  {
    path: 'finance-reports/cost-centers-group-reports/cost-centers-group-aging',
    title: 'Cost Centers Group Aging',
    loadComponent: () =>
      import('./modules/finance-reports/cost-centers-group-reports/cost-centers-group-aging/UI_Component').then(m => m.CostCenterGroupAgingComponent)
  },
  {
    path: 'finance-reports/cost-centers-group-reports/cost-centers-group-monthly-analysis',
    title: 'Cost Centers Group Monthly Analysis',
    loadComponent: () =>
      import('./modules/finance-reports/cost-centers-group-reports/cost-centers-group-monthly-analysis/UI_Component').then(m => m.CostCenterGroupMonthlyAnalysisComponent)
  },
  {
    path: 'finance-reports/cost-centers-group-reports/cost-centers-groups-ledger',
    title: 'Cost Centers Groups Ledger',
    loadComponent: () =>
      import('./modules/finance-reports/cost-centers-group-reports/cost-centers-groups-ledger/UI_Component').then(m => m.CostCenterGroupLedgerComponent)
  },

  // ================ Finance Reports > Generic Statements ================
  {
    path: 'finance-reports/generic-statements/accounts-ledger',
    title: 'Accounts Ledger',
    loadComponent: () =>
      import('./modules/finance-reports/generic-statements/accounts-ledger/UI_Component').then(m => m.AccountsLedgerComponent)
  },
  {
    path: 'finance-reports/generic-statements/aged-accounts',
    title: 'Aged Accounts',
    loadComponent: () =>
      import('./modules/finance-reports/generic-statements/aged-accounts/UI_Component').then(m => m.AgedAccountsComponent)
  },
  {
    path: 'finance-reports/generic-statements/aged-payables',
    title: 'Aged Payables',
    loadComponent: () =>
      import('./modules/finance-reports/generic-statements/aged-payables/UI_Component').then(m => m.AgedPayablesComponent)
  },
  {
    path: 'finance-reports/generic-statements/aged-receivable',
    title: 'Aged Receivable',
    loadComponent: () =>
      import('./modules/finance-reports/generic-statements/aged-receivable/UI_Component').then(m => m.AgedReceivableComponent)
  },
  {
    path: 'finance-reports/generic-statements/balance-sheet',
    title: 'Balance Sheet',
    loadComponent: () =>
      import('./modules/finance-reports/generic-statements/balance-sheet/UI_Component').then(m => m.BalanceSheetComponent)
  },
  {
    path: 'finance-reports/generic-statements/incoming-payments-log',
    title: 'Incoming Payments Log',
    loadComponent: () =>
      import('./modules/finance-reports/generic-statements/incoming-payments-log/UI_Component').then(m => m.IncomingPaymentsLogComponent)
  },
  {
    path: 'finance-reports/generic-statements/outgoing-payments-log',
    title: 'Outgoing Payments Log',
    loadComponent: () =>
      import('./modules/finance-reports/generic-statements/outgoing-payments-log/UI_Component').then(m => m.OutgoingPaymentsLogComponent)
  },
  {
    path: 'finance-reports/generic-statements/partner-statement-of-account',
    title: 'Partner Statement of Account',
    loadComponent: () =>
      import('./modules/finance-reports/generic-statements/partner-statement-of-account/UI_Component').then(m => m.PartnerStatementComponent)
  },
  {
    path: 'finance-reports/generic-statements/profit-and-loss-accounts',
    title: 'Profit & Loss Accounts',
    loadComponent: () =>
      import('./modules/finance-reports/generic-statements/profit-and-loss-accounts/UI_Component').then(m => m.ProfitLossAccountsComponent)
  },
  {
    path: 'finance-reports/generic-statements/statement-of-accounts',
    title: 'Statement of Accounts',
    loadComponent: () =>
      import('./modules/finance-reports/generic-statements/statement-of-accounts/UI_Component').then(m => m.StatementOfAccountsComponent)
  },
  {
    path: 'finance-reports/generic-statements/summary-of-main-accounts',
    title: 'Summary of Main Accounts',
    loadComponent: () =>
      import('./modules/finance-reports/generic-statements/summary-of-main-accounts/UI_Component').then(m => m.SummaryOfMainAccountsComponent)
  },

  // ===================== Finance Reports > Summary =====================
  {
    path: 'finance-reports/summary/executive-summary',
    title: 'Executive Summary',
    loadComponent: () =>
      import('./modules/finance-reports/summary/executive-summary/UI_Component').then(m => m.ExecutiveSummaryComponent)
  },
  {
    path: 'finance-reports/summary/journal-entries-summary',
    title: 'Journal Entries Summary',
    loadComponent: () =>
      import('./modules/finance-reports/summary/journal-entries-summary/UI_Component').then(m => m.JournalEntriesSummaryComponent)
  },

  // ===================== Finance > Accounts =====================
  // All 8 Finance sections are now routed (2026-07-18, Sir Rashid's go-ahead).
  // Banking's backend is live (self-contained refactor). Earnings, Expenses,
  // Payments, Requisition Status and Setting screens are wired to their
  // CANONICAL service routes — their backend sections are enabled next,
  // section by section (see LogixAI.Api.csproj).
  {
    path: 'finance/accounts/books-records-setup',
    title: 'Books Records Setup',
    loadComponent: () =>
      import('./modules/finance/accounts/books-records-setup/UI_Component').then(m => m.BooksRecordsSetupComponent)
  },
  {
    path: 'finance/accounts/chart-of-accounts',
    title: 'Chart of Accounts',
    loadComponent: () =>
      import('./modules/finance/accounts/chart-of-accounts/UI_Component').then(m => m.ChartOfAccountsComponent)
  },
  {
    path: 'finance/accounts/startup-equity',
    title: 'Startup Equity',
    loadComponent: () =>
      import('./modules/finance/accounts/startup-equity/UI_Component').then(m => m.StartupEquityComponent)
  },

  // ===================== Finance > Transactions =====================
  {
    path: 'finance/transactions/funds-transfer',
    title: 'Funds Transfer',
    loadComponent: () =>
      import('./modules/finance/transactions/funds-transfer/UI_Component').then(m => m.FundsTransferComponent)
  },
  {
    path: 'finance/transactions/funds-transfer-approval',
    title: 'Funds Transfer Approval',
    loadComponent: () =>
      import('./modules/finance/transactions/funds-transfer-approval/UI_Component').then(m => m.FundsTransferApprovalComponent)
  },
  {
    path: 'finance/transactions/general-records',
    title: 'General Records',
    loadComponent: () =>
      import('./modules/finance/transactions/general-records/UI_Component').then(m => m.GeneralRecordsComponent)
  },
  {
    path: 'finance/transactions/vouchers',
    title: 'Vouchers',
    loadComponent: () =>
      import('./modules/finance/transactions/vouchers/UI_Component').then(m => m.VouchersComponent)
  },

  // ===================== Finance > Banking =====================
  {
    path: 'finance/banking/cheque-book-configuration',
    title: 'Cheque Book Configuration',
    loadComponent: () =>
      import('./modules/finance/banking/cheque-book-configuration/UI_Component').then(m => m.ChequeBookConfigurationComponent)
  },
  {
    path: 'finance/banking/cheque-register',
    title: 'Cheque Register',
    loadComponent: () =>
      import('./modules/finance/banking/cheque-register/UI_Component').then(m => m.ChequeRegisterComponent)
  },
  {
    path: 'finance/banking/cheque-deposit',
    title: 'Cheque Deposit',
    loadComponent: () =>
      import('./modules/finance/banking/cheque-deposit/UI_Component').then(m => m.ChequeDepositComponent)
  },
  {
    path: 'finance/banking/cheque-clear',
    title: 'Cheque Clear',
    loadComponent: () =>
      import('./modules/finance/banking/cheque-clear/UI_Component').then(m => m.ChequeClearComponent)
  },
  {
    path: 'finance/banking/cheque-bounce',
    title: 'Cheque Bounce',
    loadComponent: () =>
      import('./modules/finance/banking/cheque-bounce/UI_Component').then(m => m.ChequeBounceComponent)
  },
  {
    path: 'finance/banking/cheques-return',
    title: 'Cheques Return',
    loadComponent: () =>
      import('./modules/finance/banking/cheques-return/UI_Component').then(m => m.ChequesReturnComponent)
  },
  {
    path: 'finance/banking/cheques-dashboard',
    title: 'Cheques Dashboard',
    loadComponent: () =>
      import('./modules/finance/banking/cheques-dashboard/UI_Component').then(m => m.ChequesDashboardComponent)
  },

  // ===================== Finance > Earnings =====================
  {
    path: 'finance/earnings/cash-and-bank-receipts-voucher',
    title: 'Cash & Bank Receipts Voucher',
    loadComponent: () =>
      import('./modules/finance/earnings/cash-and-bank-receipts-voucher/UI_Component').then(m => m.CashBankReceiptsComponent)
  },

  // ===================== Finance > Expenses =====================
  {
    path: 'finance/expenses/expense-type',
    title: 'Expense Type',
    loadComponent: () =>
      import('./modules/finance/expenses/expense-type/UI_Component').then(m => m.ExpenseTypeComponent)
  },
  {
    path: 'finance/expenses/expense-setup',
    title: 'Expense Setup',
    loadComponent: () =>
      import('./modules/finance/expenses/expense-setup/UI_Component').then(m => m.ExpenseSetupComponent)
  },
  {
    path: 'finance/expenses/expenses',
    title: 'Expenses',
    loadComponent: () =>
      import('./modules/finance/expenses/expenses/UI_Component').then(m => m.ExpensesComponent)
  },
  {
    path: 'finance/expenses/expenses-approval',
    title: 'Expenses Approval',
    loadComponent: () =>
      import('./modules/finance/expenses/expenses-approval/UI_Component').then(m => m.ExpensesApprovalComponent)
  },
  {
    path: 'finance/expenses/expense-payments',
    title: 'Expense Payments',
    loadComponent: () =>
      import('./modules/finance/expenses/expense-payments/UI_Component').then(m => m.ExpensePaymentsComponent)
  },

  // ===================== Finance > Payments =====================
  {
    path: 'finance/payments/cash-and-bank-payments',
    title: 'Cash & Bank Payments',
    loadComponent: () =>
      import('./modules/finance/payments/cash-and-bank-payments/UI_Component').then(m => m.CashBankPaymentsComponent)
  },

  // ================== Finance > Requisition Status ==================
  {
    path: 'finance/requisition-status/credit-notes',
    title: 'Credit Notes',
    loadComponent: () =>
      import('./modules/finance/requisition-status/credit-notes/UI_Component').then(m => m.CreditNotesComponent)
  },
  {
    path: 'finance/requisition-status/debit-notes',
    title: 'Debit Notes',
    loadComponent: () =>
      import('./modules/finance/requisition-status/debit-notes/UI_Component').then(m => m.DebitNotesComponent)
  },

  // ===================== Finance > Setting =====================
  {
    path: 'finance/setting/code-formats',
    title: 'Code Formats',
    loadComponent: () =>
      import('./modules/finance/setting/code-formats/UI_Component').then(m => m.CodeFormatsComponent)
  },
  {
    path: 'finance/setting/cost-center',
    title: 'Cost Center',
    loadComponent: () =>
      import('./modules/finance/setting/cost-center/UI_Component').then(m => m.CostCenterComponent)
  },
  {
    path: 'finance/setting/coa-step-code',
    title: 'COA Step Code',
    loadComponent: () =>
      import('./modules/finance/setting/coa-step-code/UI_Component').then(m => m.COAStepCodeComponent)
  },
  {
    path: 'finance/setting/cost-centers-groups',
    title: 'Cost Centers Groups',
    loadComponent: () =>
      import('./modules/finance/setting/cost-centers-groups/UI_Component').then(m => m.CostCentersGroupsComponent)
  },
  {
    path: 'finance/setting/balance-sheet-family',
    title: 'Balance Sheet Family',
    loadComponent: () =>
      import('./modules/finance/setting/balance-sheet-family/UI_Component').then(m => m.BalanceSheetFamilyComponent)
  },
  {
    path: 'finance/setting/gl-account-determination',
    title: 'GL Account Determination',
    loadComponent: () =>
      import('./modules/finance/setting/gl-account-determination/UI_Component').then(m => m.GlAccountDeterminationComponent)
  },
  {
    path: 'finance/setting/gl-backfill',
    title: 'GL Backfill',
    loadComponent: () =>
      import('./modules/finance/setting/gl-backfill/UI_Component').then(m => m.GlBackfillComponent)
  },

  // ===================== Reports Purchase - Sales & Stock (Phase 11) =====================
  // ---- Core ----
  {
    path: 'reports/core/procurement-records',
    title: 'Procurement Records',
    loadComponent: () =>
      import('./modules/reports/rep-procurement-records/UI_Component').then(m => m.RepProcurementRecordsComponent)
  },
  {
    path: 'reports/core/vendors-log',
    title: 'Vendors Log',
    loadComponent: () =>
      import('./modules/reports/rep-vendors-log/UI_Component').then(m => m.RepVendorsLogComponent)
  },
  {
    path: 'reports/core/customers-report',
    title: 'Customers Report',
    loadComponent: () =>
      import('./modules/reports/rep-customers-report/UI_Component').then(m => m.RepCustomersReportComponent)
  },
  {
    path: 'reports/core/sales-agent-log',
    title: 'Sales Agent Log',
    loadComponent: () =>
      import('./modules/reports/rep-sales-agent-log/UI_Component').then(m => m.RepSalesAgentLogComponent)
  },
  {
    path: 'reports/core/product-tax-log',
    title: 'Product Tax Log',
    loadComponent: () =>
      import('./modules/reports/rep-product-tax-log/UI_Component').then(m => m.RepProductTaxLogComponent)
  },
  // ---- Estimation ----
  {
    path: 'reports/estimation/sales-estimation-log',
    title: 'Sales Estimation Log',
    loadComponent: () =>
      import('./modules/reports/rep-sales-estimation-log/UI_Component').then(m => m.RepSalesEstimationLogComponent)
  },
  {
    path: 'reports/estimation/purchase-requisition-log',
    title: 'Purchase Requisition Log',
    loadComponent: () =>
      import('./modules/reports/rep-purchase-requisition-log/UI_Component').then(m => m.RepPurchaseRequisitionLogComponent)
  },
  {
    path: 'reports/estimation/purchase-requisition-product-log',
    title: 'Purchase Requisition Product Log',
    loadComponent: () =>
      import('./modules/reports/rep-purchase-requisition-product-log/UI_Component').then(m => m.RepPurchaseRequisitionProductLogComponent)
  },
  {
    path: 'reports/estimation/purchase-requisition-unsettled-log',
    title: 'Purchase Requisition Unsettled Log',
    loadComponent: () =>
      import('./modules/reports/rep-purchase-requisition-unsettled-log/UI_Component').then(m => m.RepPurchaseRequisitionUnsettledLogComponent)
  },
  // ---- Orders ----
  {
    path: 'reports/orders/sales-order-log',
    title: 'Sales Order Log',
    loadComponent: () =>
      import('./modules/reports/rep-sales-order-log/UI_Component').then(m => m.RepSalesOrderLogComponent)
  },
  {
    path: 'reports/orders/sales-order-product-log',
    title: 'Sales Order Product Log',
    loadComponent: () =>
      import('./modules/reports/rep-sales-order-product-log/UI_Component').then(m => m.RepSalesOrderProductLogComponent)
  },
  {
    path: 'reports/orders/auto-purchase-order-product-log',
    title: 'Auto Purchase Order Product Log',
    loadComponent: () =>
      import('./modules/reports/rep-auto-purchase-order-product-log/UI_Component').then(m => m.RepAutoPurchaseOrderProductLogComponent)
  },
  {
    path: 'reports/orders/purchase-order-log',
    title: 'Purchase Order Log',
    loadComponent: () =>
      import('./modules/reports/rep-purchase-order-log/UI_Component').then(m => m.RepPurchaseOrderLogComponent)
  },
  // ---- Invoice ----
  {
    path: 'reports/invoice/sales-bill-log',
    title: 'Sales Bill Log',
    loadComponent: () =>
      import('./modules/reports/rep-sales-bill-log/UI_Component').then(m => m.RepSalesBillLogComponent)
  },
  {
    path: 'reports/invoice/sales-bill-product-log',
    title: 'Sales Bill Product Log',
    loadComponent: () =>
      import('./modules/reports/rep-sales-bill-product-log/UI_Component').then(m => m.RepSalesBillProductLogComponent)
  },
  {
    path: 'reports/invoice/sales-products-statistics',
    title: 'Sales Products Statistics',
    loadComponent: () =>
      import('./modules/reports/rep-sales-products-statistics/UI_Component').then(m => m.RepSalesProductsStatisticsComponent)
  },
  {
    path: 'reports/invoice/sales-bill-product-batch-log',
    title: 'Sales Bill Product Batch Log',
    loadComponent: () =>
      import('./modules/reports/rep-sales-bill-product-batch-log/UI_Component').then(m => m.RepSalesBillProductBatchLogComponent)
  },
  {
    path: 'reports/invoice/purchase-bill-log',
    title: 'Purchase Bill Log',
    loadComponent: () =>
      import('./modules/reports/rep-purchase-bill-log/UI_Component').then(m => m.RepPurchaseBillLogComponent)
  },
  {
    path: 'reports/invoice/purchase-bill-product-log',
    title: 'Purchase Bill Product Log',
    loadComponent: () =>
      import('./modules/reports/rep-purchase-bill-product-log/UI_Component').then(m => m.RepPurchaseBillProductLogComponent)
  },
  {
    path: 'reports/invoice/sales-product-groups-statistics',
    title: 'Sales Product Groups Statistics',
    loadComponent: () =>
      import('./modules/reports/rep-sales-product-groups-statistics/UI_Component').then(m => m.RepSalesProductGroupsStatisticsComponent)
  },
  {
    path: 'reports/invoice/norcotic-product-bill-log',
    title: 'Norcotic Product Bill Log',
    loadComponent: () =>
      import('./modules/reports/rep-norcotic-product-bill-log/UI_Component').then(m => m.RepNorcoticProductBillLogComponent)
  },
  {
    path: 'reports/invoice/good-delivery-note-log',
    title: 'Good Delivery Note Log',
    loadComponent: () =>
      import('./modules/reports/rep-good-delivery-note-log/UI_Component').then(m => m.RepGoodDeliveryNoteLogComponent)
  },
  {
    path: 'reports/invoice/gdn-products-listing',
    title: 'GDN Products Listing',
    loadComponent: () =>
      import('./modules/reports/rep-gdn-products-listing/UI_Component').then(m => m.RepGdnProductsListingComponent)
  },
  {
    path: 'reports/invoice/goods-received-receipt-log',
    title: 'Goods Received Receipt Log',
    loadComponent: () =>
      import('./modules/reports/rep-goods-received-receipt-log/UI_Component').then(m => m.RepGoodsReceivedReceiptLogComponent)
  },
  {
    path: 'reports/invoice/grr-products-listing',
    title: 'GRR Products Listing',
    loadComponent: () =>
      import('./modules/reports/rep-grr-products-listing/UI_Component').then(m => m.RepGrrProductsListingComponent)
  },
  // ---- Refunds ----
  {
    path: 'reports/refunds/sales-refund-log',
    title: 'Sales Refund Log',
    loadComponent: () =>
      import('./modules/reports/rep-sales-refund-log/UI_Component').then(m => m.RepSalesRefundLogComponent)
  },
  {
    path: 'reports/refunds/purchase-refund-log',
    title: 'Purchase Refund Log',
    loadComponent: () =>
      import('./modules/reports/rep-purchase-refund-log/UI_Component').then(m => m.RepPurchaseRefundLogComponent)
  },
  // ---- Payments ----
  {
    path: 'reports/payments/sales-collection-log',
    title: 'Sales Collection Log',
    loadComponent: () =>
      import('./modules/reports/rep-sales-collection-log/UI_Component').then(m => m.RepSalesCollectionLogComponent)
  },
  {
    path: 'reports/payments/purchase-payment-log',
    title: 'Purchase Payment Log',
    loadComponent: () =>
      import('./modules/reports/rep-purchase-payment-log/UI_Component').then(m => m.RepPurchasePaymentLogComponent)
  },
  {
    path: 'reports/payments/credit-sales-due-report',
    title: 'Credit Sales Due Report',
    loadComponent: () =>
      import('./modules/reports/rep-credit-sales-due-report/UI_Component').then(m => m.RepCreditSalesDueReportComponent)
  },
  // ---- Products / Stock ----
  {
    path: 'reports/products/current-stock-multiple-uom',
    title: 'Current Stock With Multiple UOM',
    loadComponent: () =>
      import('./modules/reports/rep-current-stock-multiple-uom/UI_Component').then(m => m.RepCurrentStockMultipleUomComponent)
  },
  {
    path: 'reports/products/product-stock-keeping',
    title: 'Product Stock Keeping',
    loadComponent: () =>
      import('./modules/reports/rep-product-stock-keeping/UI_Component').then(m => m.RepProductStockKeepingComponent)
  },
  {
    path: 'reports/products/product-stock-ledger',
    title: 'Product Stock Ledger',
    loadComponent: () =>
      import('./modules/reports/rep-product-stock-ledger/UI_Component').then(m => m.RepProductStockLedgerComponent)
  },
  {
    path: 'reports/products/product-stock-as-on-date',
    title: 'Product Stock As on Date',
    loadComponent: () =>
      import('./modules/reports/rep-product-stock-as-on-date/UI_Component').then(m => m.RepProductStockAsOnDateComponent)
  },
  {
    path: 'reports/products/products-opening-stock',
    title: 'Products Opening Stock',
    loadComponent: () =>
      import('./modules/reports/rep-products-opening-stock/UI_Component').then(m => m.RepProductsOpeningStockComponent)
  },
  {
    path: 'reports/products/stock-info',
    title: 'Stock Info',
    loadComponent: () =>
      import('./modules/reports/rep-stock-info/UI_Component').then(m => m.RepStockInfoComponent)
  },
  {
    path: 'reports/products/product-damage-log',
    title: 'Product Damage Log',
    loadComponent: () =>
      import('./modules/reports/rep-product-damage-log/UI_Component').then(m => m.RepProductDamageLogComponent)
  },
  {
    path: 'reports/products/product-stock-in-house-use-log',
    title: 'Product Stock In-house Use Log',
    loadComponent: () =>
      import('./modules/reports/rep-product-stock-in-house-use-log/UI_Component').then(m => m.RepProductStockInHouseUseLogComponent)
  },
  {
    path: 'reports/products/product-stock-relocation-log',
    title: 'Product Stock Relocation Log',
    loadComponent: () =>
      import('./modules/reports/rep-product-stock-relocation-log/UI_Component').then(m => m.RepProductStockRelocationLogComponent)
  },
  {
    path: 'reports/products/products-detail-by-category',
    title: 'Products Detail By Category',
    loadComponent: () =>
      import('./modules/reports/rep-products-detail-by-category/UI_Component').then(m => m.RepProductsDetailByCategoryComponent)
  },
  {
    path: 'reports/products/stock-min-max-quantity',
    title: 'Stock with min and max quantity',
    loadComponent: () =>
      import('./modules/reports/rep-stock-min-max-quantity/UI_Component').then(m => m.RepStockMinMaxQuantityComponent)
  },
  {
    path: 'reports/products/unsold-products',
    title: 'Unsold Products',
    loadComponent: () =>
      import('./modules/reports/rep-unsold-products/UI_Component').then(m => m.RepUnsoldProductsComponent)
  },
  {
    path: 'reports/products/product-card-stock-batch',
    title: 'Product Card Stock Batch',
    loadComponent: () =>
      import('./modules/reports/rep-product-card-stock-batch/UI_Component').then(m => m.RepProductCardStockBatchComponent)
  },
  {
    path: 'reports/products/batch-expire-report',
    title: 'Batch Expire Report',
    loadComponent: () =>
      import('./modules/reports/rep-batch-expire-report/UI_Component').then(m => m.RepBatchExpireReportComponent)
  },
  // ---- VAT / TAX ----
  {
    path: 'reports/vat/tax-claim-report',
    title: 'Tax Claim Report',
    loadComponent: () =>
      import('./modules/reports/rep-tax-claim-report/UI_Component').then(m => m.RepTaxClaimReportComponent)
  },
  {
    path: 'reports/vat/collective-tax',
    title: 'Collective Tax',
    loadComponent: () =>
      import('./modules/reports/rep-collective-tax/UI_Component').then(m => m.RepCollectiveTaxComponent)
  },

  // ==== Production main menu (Phase 12, 2026-07-23) ====
  // ---- Work Center ----
  {
    path: 'production/work-center',
    title: 'Work Center',
    loadComponent: () =>
      import('./modules/production/work-center/UI_Component').then(m => m.ProdWorkCenterComponent)
  },
  {
    path: 'production/routing',
    title: 'Routing',
    loadComponent: () =>
      import('./modules/production/routing/UI_Component').then(m => m.ProdRoutingComponent)
  },
  // ---- Production ----
  {
    path: 'production/bom',
    title: 'Bill of Material',
    loadComponent: () =>
      import('./modules/production/bom/UI_Component').then(m => m.ProdBomComponent)
  },
  {
    path: 'production/production-order',
    title: 'Production Order',
    loadComponent: () =>
      import('./modules/production/production-order/UI_Component').then(m => m.ProdProductionOrderComponent)
  },
  {
    path: 'production/production-dashboard',
    title: 'Production Dashboard',
    loadComponent: () =>
      import('./modules/production/production-dashboard/UI_Component').then(m => m.ProdDashboardComponent)
  },
  {
    path: 'production/job-card',
    title: 'Job Card',
    loadComponent: () =>
      import('./modules/production/job-card/UI_Component').then(m => m.ProdJobCardComponent)
  },
  // ---- Material Management ----
  {
    path: 'production/material-receipt',
    title: 'Material Receipt',
    loadComponent: () =>
      import('./modules/production/material-receipt/UI_Component').then(m => m.ProdMaterialReceiptComponent)
  },
  {
    path: 'production/material-receipt-refund',
    title: 'Material Receipt Refund',
    loadComponent: () =>
      import('./modules/production/material-receipt-refund/UI_Component').then(m => m.ProdMaterialReceiptRefundComponent)
  },
  {
    path: 'production/material-damage',
    title: 'Material Damage',
    loadComponent: () =>
      import('./modules/production/material-damage/UI_Component').then(m => m.ProdMaterialDamageComponent)
  },
  // ---- Finished Goods ----
  {
    path: 'production/production-receipt',
    title: 'Production Receipt',
    loadComponent: () =>
      import('./modules/production/production-receipt/UI_Component').then(m => m.ProdProductionReceiptComponent)
  },
  // ---- Resource Management ----
  {
    path: 'production/resource-uom',
    title: 'Resource UOM',
    loadComponent: () =>
      import('./modules/production/resource-uom/UI_Component').then(m => m.ProdResourceUomComponent)
  },
  {
    path: 'production/resource-master',
    title: 'Resource Master Data',
    loadComponent: () =>
      import('./modules/production/resource-master/UI_Component').then(m => m.ProdResourceMasterComponent)
  },
  // ---- Scrap & By-Product Management ----
  {
    path: 'production/scrap-management',
    title: 'Scrap & By-Product Management',
    loadComponent: () =>
      import('./modules/production/scrap-management/UI_Component').then(m => m.ProdScrapManagementComponent)
  },
  // ---- Production Processing ----
  {
    path: 'production/production-processing',
    title: 'Production Processing',
    loadComponent: () =>
      import('./modules/production/production-processing/UI_Component').then(m => m.ProdProductionProcessingComponent)
  },
  // ---- Setting ----
  {
    path: 'production/setting/code-formats',
    title: 'Code Formats',
    loadComponent: () =>
      import('./modules/production/setting-code-formats/UI_Component').then(m => m.ProdSettingCodeFormatsComponent)
  },
  {
    path: 'production/setting/production-posting-setup',
    title: 'Production Posting Setup',
    loadComponent: () =>
      import('./modules/production/setting-posting-setup/UI_Component').then(m => m.ProdSettingPostingSetupComponent)
  },
  {
    path: 'production/setting/production-setting',
    title: 'Production Setting',
    loadComponent: () =>
      import('./modules/production/setting-production-setting/UI_Component').then(m => m.ProdSettingProductionSettingComponent)
  },
  // ---- Packing Industry ----
  {
    path: 'production/production-types',
    title: 'Production Types',
    loadComponent: () =>
      import('./modules/production/production-types/UI_Component').then(m => m.ProdProductionTypesComponent)
  },
  // ---- Production Reports ----
  { path: 'production/reports/finished-products', title: 'Finished Products Report', loadComponent: () => import('./modules/production/rep-finished-products/UI_Component').then(m => m.ProdRepFinishedProductsComponent) },
  { path: 'production/reports/work-centers', title: 'Work Centers Report', loadComponent: () => import('./modules/production/rep-work-centers/UI_Component').then(m => m.ProdRepWorkCentersComponent) },
  { path: 'production/reports/raw-material', title: 'Raw Material Report', loadComponent: () => import('./modules/production/rep-raw-material/UI_Component').then(m => m.ProdRepRawMaterialComponent) },
  { path: 'production/reports/raw-material-refund', title: 'Raw Material Refund Report', loadComponent: () => import('./modules/production/rep-raw-material-refund/UI_Component').then(m => m.ProdRepRawMaterialRefundComponent) },
  { path: 'production/reports/production-processing', title: 'Production Processing Report', loadComponent: () => import('./modules/production/rep-production-processing/UI_Component').then(m => m.ProdRepProductionProcessingComponent) },
  { path: 'production/reports/scrap-byproduct', title: 'Scrap / By-Product Report', loadComponent: () => import('./modules/production/rep-scrap-byproduct/UI_Component').then(m => m.ProdRepScrapByProductComponent) },
  { path: 'production/reports/resources', title: 'Resources Report', loadComponent: () => import('./modules/production/rep-resources/UI_Component').then(m => m.ProdRepResourcesComponent) },
  { path: 'production/reports/damage-products', title: 'Damage Products Report', loadComponent: () => import('./modules/production/rep-damage-products/UI_Component').then(m => m.ProdRepDamageProductsComponent) },
  { path: 'production/reports/production-order', title: 'Production Order Report', loadComponent: () => import('./modules/production/rep-production-order/UI_Component').then(m => m.ProdRepProductionOrderComponent) },

  // ===== Quality Control main menu (Phase 13, 2026-07-24) =====
  { path: 'quality-control/qc-criteria', title: 'QC Criteria', loadComponent: () => import('./modules/quality-control/qc-criteria/UI_Component').then(m => m.QcCriteriaComponent) },
  { path: 'quality-control/inspection-request', title: 'Inspection Request', loadComponent: () => import('./modules/quality-control/inspection-request/UI_Component').then(m => m.QcInspectionRequestComponent) },
  { path: 'quality-control/sample-collection', title: 'Sample Collection', loadComponent: () => import('./modules/quality-control/sample-collection/UI_Component').then(m => m.QcSampleCollectionComponent) },
  { path: 'quality-control/inspection-results', title: 'Quality Inspection Results', loadComponent: () => import('./modules/quality-control/inspection-results/UI_Component').then(m => m.QcInspectionResultsComponent) },
  { path: 'quality-control/reports/product-qc-criteria', title: 'Product QC Criteria Report', loadComponent: () => import('./modules/quality-control/rep-product-qc-criteria/UI_Component').then(m => m.QcRepProductCriteriaComponent) },
  { path: 'quality-control/reports/pending-qc-request', title: 'Pending QC Request Report', loadComponent: () => import('./modules/quality-control/rep-pending-qc-request/UI_Component').then(m => m.QcRepPendingRequestComponent) },
  { path: 'quality-control/reports/qc-sample-usage', title: 'QC Sample Usage Summary', loadComponent: () => import('./modules/quality-control/rep-qc-sample-usage/UI_Component').then(m => m.QcRepSampleUsageComponent) },
  { path: 'quality-control/reports/inspection-result', title: 'Inspection Result Report', loadComponent: () => import('./modules/quality-control/rep-inspection-result/UI_Component').then(m => m.QcRepInspectionResultComponent) },

  // ===== Asset Management main menu (Phase 14, 2026-07-24) =====
  { path: 'asset-management/asset-specifications', title: 'Asset Specifications', loadComponent: () => import('./modules/asset-management/asset-specifications/UI_Component').then(m => m.AssetSpecificationsComponent) },
  { path: 'asset-management/asset-registration', title: 'Asset Registration', loadComponent: () => import('./modules/asset-management/asset-registration/UI_Component').then(m => m.AssetRegistrationComponent) },
  { path: 'asset-management/asset-movement', title: 'Asset Movement', loadComponent: () => import('./modules/asset-management/asset-movement/UI_Component').then(m => m.AssetMovementComponent) },
  { path: 'asset-management/asset-depreciation', title: 'Asset Depreciation', loadComponent: () => import('./modules/asset-management/asset-depreciation/UI_Component').then(m => m.AssetDepreciationComponent) },
  { path: 'asset-management/asset-appreciation', title: 'Asset Appreciation', loadComponent: () => import('./modules/asset-management/asset-appreciation/UI_Component').then(m => m.AssetAppreciationComponent) },
  { path: 'asset-management/reports/registration', title: 'Asset Registration Report', loadComponent: () => import('./modules/asset-management/rep-registration/UI_Component').then(m => m.AssetRepRegistrationComponent) },
  { path: 'asset-management/reports/movement', title: 'Asset Movement Report', loadComponent: () => import('./modules/asset-management/rep-movement/UI_Component').then(m => m.AssetRepMovementComponent) },
  { path: 'asset-management/reports/appreciation', title: 'Appreciation Report', loadComponent: () => import('./modules/asset-management/rep-appreciation/UI_Component').then(m => m.AssetRepAppreciationComponent) },
  { path: 'asset-management/reports/depreciation', title: 'Asset Depreciation Report', loadComponent: () => import('./modules/asset-management/rep-depreciation/UI_Component').then(m => m.AssetRepDepreciationComponent) },

  // ===== HRM Reports (dedicated top-level menu — 17 read-only reports over HR.* tables) =====
  { path: 'hrm-reports/employee-directory', title: 'Employee Directory', loadComponent: () => import('./modules/hrm-reports/rep-employee-directory/UI_Component').then(m => m.HrmRepEmployeeDirectoryComponent) },
  { path: 'hrm-reports/contract', title: 'Contract Report', loadComponent: () => import('./modules/hrm-reports/rep-contract/UI_Component').then(m => m.HrmRepContractComponent) },
  { path: 'hrm-reports/custody', title: 'Custody Report', loadComponent: () => import('./modules/hrm-reports/rep-custody/UI_Component').then(m => m.HrmRepCustodyComponent) },
  { path: 'hrm-reports/document-expiry', title: 'Employee Document Expiry', loadComponent: () => import('./modules/hrm-reports/rep-document-expiry/UI_Component').then(m => m.HrmRepDocumentExpiryComponent) },
  { path: 'hrm-reports/payroll-register', title: 'Payroll Register', loadComponent: () => import('./modules/hrm-reports/rep-payroll-register/UI_Component').then(m => m.HrmRepPayrollRegisterComponent) },
  { path: 'hrm-reports/payroll-allowance', title: 'Payroll Allowance Report', loadComponent: () => import('./modules/hrm-reports/rep-payroll-allowance/UI_Component').then(m => m.HrmRepPayrollAllowanceComponent) },
  { path: 'hrm-reports/payroll-deduction', title: 'Payroll Deduction Report', loadComponent: () => import('./modules/hrm-reports/rep-payroll-deduction/UI_Component').then(m => m.HrmRepPayrollDeductionComponent) },
  { path: 'hrm-reports/allowance-structure', title: 'Allowance Structure Report', loadComponent: () => import('./modules/hrm-reports/rep-allowance-structure/UI_Component').then(m => m.HrmRepAllowanceStructureComponent) },
  { path: 'hrm-reports/deduction-structure', title: 'Deduction Structure Report', loadComponent: () => import('./modules/hrm-reports/rep-deduction-structure/UI_Component').then(m => m.HrmRepDeductionStructureComponent) },
  { path: 'hrm-reports/salary-tax', title: 'Salary Tax Report', loadComponent: () => import('./modules/hrm-reports/rep-salary-tax/UI_Component').then(m => m.HrmRepSalaryTaxComponent) },
  { path: 'hrm-reports/loan', title: 'Loan Report', loadComponent: () => import('./modules/hrm-reports/rep-loan/UI_Component').then(m => m.HrmRepLoanComponent) },
  { path: 'hrm-reports/advance', title: 'Advance Salary Report', loadComponent: () => import('./modules/hrm-reports/rep-advance/UI_Component').then(m => m.HrmRepAdvanceComponent) },
  { path: 'hrm-reports/attendance', title: 'Attendance Report', loadComponent: () => import('./modules/hrm-reports/rep-attendance/UI_Component').then(m => m.HrmRepAttendanceComponent) },
  { path: 'hrm-reports/leave', title: 'Leave Report', loadComponent: () => import('./modules/hrm-reports/rep-leave/UI_Component').then(m => m.HrmRepLeaveComponent) },
  { path: 'hrm-reports/leave-balance', title: 'Leave Balance Report', loadComponent: () => import('./modules/hrm-reports/rep-leave-balance/UI_Component').then(m => m.HrmRepLeaveBalanceComponent) },
  { path: 'hrm-reports/assets', title: 'Assets Report', loadComponent: () => import('./modules/hrm-reports/rep-assets/UI_Component').then(m => m.HrmRepAssetsComponent) },
  { path: 'hrm-reports/eosb', title: 'EOSB / Settlement Report', loadComponent: () => import('./modules/hrm-reports/rep-eosb/UI_Component').then(m => m.HrmRepEosbComponent) },

  // ===== CRM (Configurations, Leads pipeline, CRM Reports) =====
  { path: 'crm/companies', title: 'Companies', loadComponent: () => import('./modules/crm/companies/UI_Component').then(m => m.CrmCompaniesComponent) },
  { path: 'crm/contacts', title: 'Contacts', loadComponent: () => import('./modules/crm/contacts/UI_Component').then(m => m.CrmContactsComponent) },
  { path: 'crm/stages', title: 'Pipeline Stages', loadComponent: () => import('./modules/crm/stages/UI_Component').then(m => m.CrmStagesComponent) },
  { path: 'crm/activity-type', title: 'Activity Types', loadComponent: () => import('./modules/crm/activity-type/UI_Component').then(m => m.CrmActivityTypeComponent) },
  { path: 'crm/leads', title: 'Leads Pipeline', loadComponent: () => import('./modules/crm/leads/UI_Component').then(m => m.CrmLeadsComponent) },
  { path: 'crm/calendar', title: 'Activity Calendar', loadComponent: () => import('./modules/crm/calendar/UI_Component').then(m => m.CrmCalendarComponent) },
  { path: 'crm/reports/leads', title: 'Leads Report', loadComponent: () => import('./modules/crm/rep-leads/UI_Component').then(m => m.CrmRepLeadsComponent) },
  { path: 'crm/reports/activity', title: 'Activity Report', loadComponent: () => import('./modules/crm/rep-activity/UI_Component').then(m => m.CrmRepActivityComponent) },

  // ===== Customer Support Center (CSC) — helpdesk department =====
  { path: 'csc/dashboard', title: 'Support Dashboard', loadComponent: () => import('./modules/csc/dashboard/UI_Component').then(m => m.CscDashboardComponent) },
  { path: 'csc/tickets', title: 'Support Tickets', loadComponent: () => import('./modules/csc/tickets/UI_Component').then(m => m.CscTicketsComponent) },
  { path: 'csc/categories', title: 'Ticket Categories', loadComponent: () => import('./modules/csc/_shared/csc-master.component').then(m => m.CscMasterComponent), data: { cfg: CSC_CATEGORIES } },
  { path: 'csc/priorities', title: 'Ticket Priorities', loadComponent: () => import('./modules/csc/_shared/csc-master.component').then(m => m.CscMasterComponent), data: { cfg: CSC_PRIORITIES } },
  { path: 'csc/statuses', title: 'Ticket Statuses', loadComponent: () => import('./modules/csc/_shared/csc-master.component').then(m => m.CscMasterComponent), data: { cfg: CSC_STATUSES } },
  { path: 'csc/agents', title: 'Support Agents', loadComponent: () => import('./modules/csc/_shared/csc-master.component').then(m => m.CscMasterComponent), data: { cfg: CSC_AGENTS } },
  { path: 'csc/sla-policies', title: 'SLA Policies', loadComponent: () => import('./modules/csc/_shared/csc-master.component').then(m => m.CscMasterComponent), data: { cfg: CSC_SLA } },
  { path: 'csc/canned-responses', title: 'Canned Responses', loadComponent: () => import('./modules/csc/_shared/csc-master.component').then(m => m.CscMasterComponent), data: { cfg: CSC_CANNED } },
  { path: 'csc/kb-articles', title: 'Knowledge Base', loadComponent: () => import('./modules/csc/_shared/csc-master.component').then(m => m.CscMasterComponent), data: { cfg: CSC_KB } },
  { path: 'csc/reports/tickets', title: 'Tickets Report', loadComponent: () => import('./modules/csc/_shared/csc-report.component').then(m => m.CscReportComponent), data: { cfg: CSC_REP_TICKETS } },
  { path: 'csc/reports/agent-performance', title: 'Agent Performance', loadComponent: () => import('./modules/csc/_shared/csc-report.component').then(m => m.CscReportComponent), data: { cfg: CSC_REP_AGENT } },
  { path: 'csc/reports/sla-compliance', title: 'SLA Compliance', loadComponent: () => import('./modules/csc/_shared/csc-report.component').then(m => m.CscReportComponent), data: { cfg: CSC_REP_SLA } },

  // ===== Logix AI POS (Salesman Booking + Counter/Cashier -> GL spine) =====
  { path: 'pos/booking', title: 'Salesman Booking', loadComponent: () => import('./modules/pos/booking/UI_Component').then(m => m.PosBookingComponent) },
  { path: 'pos/counter-queue', title: 'Counter Queue', loadComponent: () => import('./modules/pos/counter-queue/UI_Component').then(m => m.PosCounterQueueComponent) },
  { path: 'pos/cashier', title: 'Cashier', loadComponent: () => import('./modules/pos/cashier/UI_Component').then(m => m.PosCashierComponent) },
  { path: 'pos/counters', title: 'Terminals / Counters', loadComponent: () => import('./modules/pos/counters/UI_Component').then(m => m.PosCountersComponent) },
  { path: 'pos/users', title: 'POS Users', loadComponent: () => import('./modules/pos/users/UI_Component').then(m => m.PosUsersComponent) },
  { path: 'pos/product-families', title: 'Product Families', loadComponent: () => import('./modules/pos/product-families/UI_Component').then(m => m.PosProductFamiliesComponent) },
  { path: 'pos/printer-family', title: 'Printer Family', loadComponent: () => import('./modules/pos/printer-family/UI_Component').then(m => m.PosPrinterFamilyComponent) },
  { path: 'pos/naming-conventions', title: 'Naming Conventions', loadComponent: () => import('./modules/pos/naming-conventions/UI_Component').then(m => m.PosNamingConventionsComponent) },
  { path: 'pos/bridge-servers', title: 'Bridge Servers', loadComponent: () => import('./modules/pos/bridge-servers/UI_Component').then(m => m.PosBridgeServersComponent) },
  { path: 'pos/bill-format', title: 'Bill Format', loadComponent: () => import('./modules/pos/bill-format/UI_Component').then(m => m.PosBillFormatComponent) },
  { path: 'pos/weigh-scale-format', title: 'Weigh Scale Format', loadComponent: () => import('./modules/pos/weigh-scale-format/UI_Component').then(m => m.PosWeighScaleFormatComponent) },

  // ===== Online Store (ECOMM back-office) =====
  { path: 'online-store/stores', title: 'Stores', loadComponent: () => import('./modules/online-store/stores/UI_Component').then(m => m.EcommStoresComponent) },
  { path: 'online-store/catalog', title: 'Store Catalog', loadComponent: () => import('./modules/online-store/catalog/UI_Component').then(m => m.EcommCatalogComponent) },
  { path: 'online-store/content', title: 'Content Pages', loadComponent: () => import('./modules/online-store/content/UI_Component').then(m => m.EcommContentComponent) },
  { path: 'online-store/orders', title: 'Online Orders', loadComponent: () => import('./modules/online-store/orders/UI_Component').then(m => m.EcommOrdersComponent) },
  { path: 'online-store/collections', title: 'Rider Collections', loadComponent: () => import('./modules/online-store/collections/UI_Component').then(m => m.EcommCollectionsComponent) },
  { path: 'online-store/riders', title: 'Riders', loadComponent: () => import('./modules/online-store/riders/UI_Component').then(m => m.EcommRidersComponent) },
  { path: 'online-store/delivery-charges', title: 'Delivery Charges', loadComponent: () => import('./modules/online-store/delivery-charges/UI_Component').then(m => m.EcommDeliveryChargesComponent) },
  { path: 'online-store/shipments', title: 'Shipments', loadComponent: () => import('./modules/online-store/shipments/UI_Component').then(m => m.EcommShipmentsComponent) },
  { path: 'online-store/banners', title: 'Banner Products', loadComponent: () => import('./modules/online-store/banners/UI_Component').then(m => m.EcommBannersComponent) },
  { path: 'online-store/discounts', title: 'Discounts & Promotions', loadComponent: () => import('./modules/online-store/discounts/UI_Component').then(m => m.EcommDiscountsComponent) },
  { path: 'online-store/menu-setup', title: 'Menu Setup', loadComponent: () => import('./modules/online-store/menu-setup/UI_Component').then(m => m.EcommMenuSetupComponent) },
  { path: 'online-store/products', title: 'Products (Catalog)', loadComponent: () => import('./modules/online-store/products/UI_Component').then(m => m.EcommProductsComponent) },

  // ===== Human Resource Management (Phase 15) — routes added per section =====
  // Section 1: Configurations
  { path: 'hrm/nationalities', title: 'Nationalities', loadComponent: () => import('./modules/hrm/nationalities/UI_Component').then(m => m.HrNationalitiesComponent) },
  { path: 'hrm/religions', title: 'Religions', loadComponent: () => import('./modules/hrm/religions/UI_Component').then(m => m.HrReligionsComponent) },
  { path: 'hrm/locations', title: 'Locations', loadComponent: () => import('./modules/hrm/locations/UI_Component').then(m => m.HrLocationsComponent) },
  { path: 'hrm/branches', title: 'Branches', loadComponent: () => import('./modules/hrm/branches/UI_Component').then(m => m.HrBranchesComponent) },
  { path: 'hrm/departments', title: 'Departments', loadComponent: () => import('./modules/hrm/departments/UI_Component').then(m => m.HrDepartmentsComponent) },
  { path: 'hrm/designations', title: 'Designations', loadComponent: () => import('./modules/hrm/designations/UI_Component').then(m => m.HrDesignationsComponent) },
  { path: 'hrm/grades', title: 'Grades', loadComponent: () => import('./modules/hrm/grades/UI_Component').then(m => m.HrGradesComponent) },
  { path: 'hrm/asset-types', title: 'Asset Types', loadComponent: () => import('./modules/hrm/asset-types/UI_Component').then(m => m.HrAssetTypesComponent) },
  { path: 'hrm/shift-schedules', title: 'Shift Schedules', loadComponent: () => import('./modules/hrm/shift-schedules/UI_Component').then(m => m.HrShiftSchedulesComponent) },
  // Section 2: General
  { path: 'hrm/official-document', title: 'Official Document', loadComponent: () => import('./modules/hrm/official-document/UI_Component').then(m => m.HrOfficialDocumentComponent) },
  { path: 'hrm/payroll-tax-structure', title: 'Payroll Tax Structure', loadComponent: () => import('./modules/hrm/payroll-tax-structure/UI_Component').then(m => m.HrPayrollTaxStructureComponent) },
  // Section 3: Employee Management
  { path: 'hrm/employee-profile', title: 'Employee Profile', loadComponent: () => import('./modules/hrm/employee-profile/UI_Component').then(m => m.HrEmployeeProfileComponent) },
  { path: 'hrm/employee-contract', title: 'Contract', loadComponent: () => import('./modules/hrm/employee-contract/UI_Component').then(m => m.HrEmployeeContractComponent) },
  { path: 'hrm/employee-custody', title: 'Custody', loadComponent: () => import('./modules/hrm/employee-custody/UI_Component').then(m => m.HrEmployeeCustodyComponent) },
  { path: 'hrm/employee-notices', title: 'Notices', loadComponent: () => import('./modules/hrm/employee-notices/UI_Component').then(m => m.HrEmployeeNoticesComponent) },
  { path: 'hrm/employee-expenses', title: 'Employee Expenses', loadComponent: () => import('./modules/hrm/employee-expenses/UI_Component').then(m => m.HrEmployeeExpensesComponent) },
  { path: 'hrm/employee-dependents', title: 'Employee Dependents', loadComponent: () => import('./modules/hrm/employee-dependents/UI_Component').then(m => m.HrEmployeeDependentsComponent) },
  { path: 'hrm/employee-resignation', title: 'Resignation / Termination', loadComponent: () => import('./modules/hrm/employee-resignation/UI_Component').then(m => m.HrEmployeeResignationComponent) },
  { path: 'hrm/employee-transfer', title: 'Employee Transfer', loadComponent: () => import('./modules/hrm/employee-transfer/UI_Component').then(m => m.HrEmployeeTransferComponent) },
  { path: 'hrm/employee-promotion', title: 'Employee Promotion', loadComponent: () => import('./modules/hrm/employee-promotion/UI_Component').then(m => m.HrEmployeePromotionComponent) },
  // Section 4: Payroll
  { path: 'hrm/allowance-structure', title: 'Allowance Structure', loadComponent: () => import('./modules/hrm/allowance-structure/UI_Component').then(m => m.HrAllowanceStructureComponent) },
  { path: 'hrm/deduction-structure', title: 'Deduction Structure', loadComponent: () => import('./modules/hrm/deduction-structure/UI_Component').then(m => m.HrDeductionStructureComponent) },
  { path: 'hrm/payroll-generator', title: 'Payroll Generator', loadComponent: () => import('./modules/hrm/payroll-generator/UI_Component').then(m => m.HrPayrollGeneratorComponent) },
  { path: 'hrm/payroll-details', title: 'Payroll Details', loadComponent: () => import('./modules/hrm/payroll-details/UI_Component').then(m => m.HrPayrollDetailsComponent) },
  { path: 'hrm/loan-management', title: 'Loan Management', loadComponent: () => import('./modules/hrm/loan-management/UI_Component').then(m => m.HrLoanManagementComponent) },
  { path: 'hrm/advance-salary', title: 'Advance Salary', loadComponent: () => import('./modules/hrm/advance-salary/UI_Component').then(m => m.HrAdvanceSalaryComponent) },
  // Section 5: Service Setup
  { path: 'hrm/eosb', title: 'EOSB', loadComponent: () => import('./modules/hrm/eosb/UI_Component').then(m => m.HrEosbComponent) },
  { path: 'hrm/eosb-structure', title: 'EOSB Structure', loadComponent: () => import('./modules/hrm/eosb-structure/UI_Component').then(m => m.HrEosbStructureComponent) },
  // Section 6: Payroll Setup
  { path: 'hrm/pay-group', title: 'Pay Group', loadComponent: () => import('./modules/hrm/pay-group/UI_Component').then(m => m.HrPayGroupComponent) },
  { path: 'hrm/allowances', title: 'Allowances', loadComponent: () => import('./modules/hrm/allowances/UI_Component').then(m => m.HrAllowancesComponent) },
  { path: 'hrm/deduction-category', title: 'Deduction Category', loadComponent: () => import('./modules/hrm/deduction-category/UI_Component').then(m => m.HrDeductionCategoryComponent) },
  { path: 'hrm/payroll-posting-setup', title: 'Payroll Posting Setup', loadComponent: () => import('./modules/hrm/payroll-posting-setup/UI_Component').then(m => m.HrPayrollPostingSetupComponent) },
  { path: 'hrm/payroll-settings', title: 'Payroll Settings', loadComponent: () => import('./modules/hrm/payroll-settings/UI_Component').then(m => m.HrPayrollSettingsComponent) },
  // Section 7: Service Transactions
  { path: 'hrm/withdrawl', title: 'Withdrawl', loadComponent: () => import('./modules/hrm/withdrawl/UI_Component').then(m => m.HrWithdrawlComponent) },
  { path: 'hrm/attendance', title: 'Attendance', loadComponent: () => import('./modules/hrm/attendance/UI_Component').then(m => m.HrAttendanceComponent) },
  { path: 'hrm/assets-entry', title: 'Assets Entry', loadComponent: () => import('./modules/hrm/assets-entry/UI_Component').then(m => m.HrAssetsEntryComponent) },
  // Section 8: Service Letter
  { path: 'hrm/letterhead', title: 'Letterhead', loadComponent: () => import('./modules/hrm/letterhead/UI_Component').then(m => m.HrLetterheadComponent) },
  { path: 'hrm/employee-letter', title: 'Employee Letter', loadComponent: () => import('./modules/hrm/employee-letter/UI_Component').then(m => m.HrEmployeeLetterComponent) },
  // Section 9: Leave Management
  { path: 'hrm/leave-type', title: 'Leave Type', loadComponent: () => import('./modules/hrm/leave-type/UI_Component').then(m => m.HrLeaveTypeComponent) },
  { path: 'hrm/vacation-structure', title: 'Vacation Structure', loadComponent: () => import('./modules/hrm/vacation-structure/UI_Component').then(m => m.HrVacationStructureComponent) },
  { path: 'hrm/leave', title: 'Leave', loadComponent: () => import('./modules/hrm/leave/UI_Component').then(m => m.HrLeaveComponent) },
  { path: 'hrm/leave-apply', title: 'Leave Application', loadComponent: () => import('./modules/hrm/leave-apply/UI_Component').then(m => m.LeaveApplyComponent) },
  { path: 'approvals', title: 'Approvals', loadComponent: () => import('./modules/approvals/UI_Component').then(m => m.ApprovalsComponent) },
  { path: 'hrm/weekend-structure', title: 'Weekend Structure', loadComponent: () => import('./modules/hrm/weekend-structure/UI_Component').then(m => m.HrWeekendStructureComponent) },
  { path: 'hrm/public-holidays', title: 'Public Holidays', loadComponent: () => import('./modules/hrm/public-holidays/UI_Component').then(m => m.HrPublicHolidaysComponent) },
  // Section 10: Directory
  { path: 'hrm/employee-directory', title: 'Employee Directory', loadComponent: () => import('./modules/hrm/employee-directory/UI_Component').then(m => m.HrEmployeeDirectoryComponent) },
  // Section 11: Task Management
  { path: 'hrm/task-types', title: 'Task Types', loadComponent: () => import('./modules/hrm/task-types/UI_Component').then(m => m.HrTaskTypesComponent) },
  { path: 'hrm/task-status', title: 'Task Status', loadComponent: () => import('./modules/hrm/task-status/UI_Component').then(m => m.HrTaskStatusComponent) },
  { path: 'hrm/employee-tasks', title: 'Employee Tasks', loadComponent: () => import('./modules/hrm/employee-tasks/UI_Component').then(m => m.HrEmployeeTasksComponent) },
  { path: 'hrm/projects', title: 'Projects', loadComponent: () => import('./modules/hrm/projects/UI_Component').then(m => m.HrProjectsComponent) },
  { path: 'hrm/complete-task', title: 'Complete Task', loadComponent: () => import('./modules/hrm/complete-task/UI_Component').then(m => m.HrCompleteTaskComponent) },
  // Section 12: Settings
  { path: 'hrm/code-formats', title: 'Code Formats', loadComponent: () => import('./modules/hrm/code-formats/UI_Component').then(m => m.HrCodeFormatsComponent) },
  // Rec-4: Expiry Alerts dashboard (notification service)
  { path: 'hrm/alerts', title: 'Expiry Alerts', loadComponent: () => import('./modules/hrm/alerts/UI_Component').then(m => m.HrAlertsComponent) },

  // ===== Rec-3 · ESS / MSS Self-Service Portal (2026-07-28) =====
  // Employee Self-Service — all scoped server-side to the signed-in employee.
  { path: 'self-service/my-profile', title: 'My Profile', loadComponent: () => import('./modules/self-service/my-profile/UI_Component').then(m => m.MyProfileComponent) },
  { path: 'self-service/my-payslips', title: 'My Payslips', loadComponent: () => import('./modules/self-service/my-payslips/UI_Component').then(m => m.MyPayslipsComponent) },
  { path: 'self-service/my-leave', title: 'My Leave', loadComponent: () => import('./modules/self-service/my-leave/UI_Component').then(m => m.MyLeaveComponent) },
  { path: 'self-service/my-attendance', title: 'My Attendance', loadComponent: () => import('./modules/self-service/my-attendance/UI_Component').then(m => m.MyAttendanceComponent) },
  { path: 'self-service/my-loans', title: 'My Loans & Advances', loadComponent: () => import('./modules/self-service/my-loans/UI_Component').then(m => m.MyLoansComponent) },
  { path: 'self-service/my-tasks', title: 'My Tasks', loadComponent: () => import('./modules/self-service/my-tasks/UI_Component').then(m => m.MyTasksComponent) },
  // Manager Self-Service — team scoped server-side to the signed-in manager's direct reports.
  { path: 'self-service/team', title: 'My Team', loadComponent: () => import('./modules/self-service/team/UI_Component').then(m => m.TeamComponent) },
  { path: 'self-service/team-leave', title: 'Team Leave', loadComponent: () => import('./modules/self-service/team-leave/UI_Component').then(m => m.TeamLeaveComponent) },
  { path: 'self-service/team-payroll', title: 'Team Payroll', loadComponent: () => import('./modules/self-service/team-payroll/UI_Component').then(m => m.TeamPayrollComponent) },

      { path: '**', redirectTo: '' }
    ]
  }
];
